import { useAppConfiguration, useGraphqlQuery } from '@mews-commander/data-access';
import {
    getGraphqlFragment,
    graphql,
    GuestSummary,
    GuestSummaryByIdResultQuery,
    Membership,
    ReservationSummary,
} from '@mews-commander/guest-profile';
import {
    LoyaltyMembershipSummaryFieldsFragment,
    LoyaltyProgramFieldsFragment,
    LoyaltyTierFieldsFragment,
} from '../../../../common';
import { GuestProfileBasicFieldsFragment } from '../../../fragments/guestProfileFragment';
import {
    ClassificationFieldsFragment,
    ReservationSummaryFieldsFragment,
} from '../fragments/guestSummaryDetailFragments';

const GuestSummaryByIdQueryDocument = graphql(`
    query guestSummaryByIdResult($guestId: UUID!) {
        guestSummaryById(guestId: $guestId) {
            classifications {
                ...ClassificationFields
            }
            loyaltyMemberships {
                ...LoyaltyMembershipSummaryFields
            }
            profile {
                ...GuestProfileBasicFields
            }
            reservations {
                ...ReservationSummaryFields
            }
        }
    }
`);

const transformToGuestSummary = (data: GuestSummaryByIdResultQuery): Omit<GuestSummary, 'guestId' | 'analytics'> => {
    const { guestSummaryById } = data;

    const profile = getGraphqlFragment(GuestProfileBasicFieldsFragment, guestSummaryById.profile);
    const classifications = getGraphqlFragment(ClassificationFieldsFragment, guestSummaryById.classifications);
    const loyaltyMemberships: Membership[] = guestSummaryById.loyaltyMemberships.map((membershipFragment) => {
        const membership = getGraphqlFragment(LoyaltyMembershipSummaryFieldsFragment, membershipFragment);
        const program = getGraphqlFragment(LoyaltyProgramFieldsFragment, membership.program);
        const tier = getGraphqlFragment(LoyaltyTierFieldsFragment, membership.tier);

        return {
            ...membership,
            program: { ...program },
            tier: tier ? { ...tier } : undefined,
        } satisfies Membership;
    });
    const reservations: ReservationSummary[] = guestSummaryById.reservations.map((reservationSummary) => {
        const reservation = getGraphqlFragment(ReservationSummaryFieldsFragment, reservationSummary);

        return { ...reservation } satisfies ReservationSummary;
    });

    return {
        profile,
        loyaltyMemberships,
        classifications,
        reservations,
    };
};

export const useGuestSummaryDetail = (guestId: string) => {
    const { guestProfileApiUrl } = useAppConfiguration();
    const { data, ...props } = useGraphqlQuery(
        guestProfileApiUrl,
        {
            document: GuestSummaryByIdQueryDocument,
            variables: { guestId },
        },
        { enabled: !!guestId, staleTime: 0 }
    );

    return {
        ...props,
        data: data ? transformToGuestSummary(data) : undefined,
    };
};
